import { resolvePrizeTypeLabel } from './prizeTypeLabels'

export interface PayoutRatesTableRow {
  name: string
  payout: number
  discount: number
  order: number
}

export interface PrizeConfigForPayoutTable {
  prizeTypeCode: string
  prizeTypeName?: string
  payoutRate: number
  discount?: number
  isActive?: boolean
  displayOrder?: number
}

const PRIZE_TYPE_DISPLAY_ORDER: Record<string, number> = {
  '2_DIGIT_TOP': 1,
  '3_DIGIT_TOP': 2,
  '3_DIGIT_TOD': 3,
  '2_DIGIT_TOD': 4,
  RUNNING_TOP: 5,
  RUNNING_BOTTOM: 6,
  '2_DIGIT_BOTTOM': 7,
  '3_DIGIT_BOTTOM': 8,
  '4_DIGIT_TOP': 9,
  '4_DIGIT_TOD': 10,
  '5_DIGIT_TOD': 11,
  FIXED_POSITION_ONES: 12,
  FIXED_POSITION_TENS: 13,
  FIXED_POSITION_HUNDREDS: 14,
}

const FALLBACK_ROWS: PayoutRatesTableRow[] = [
  { name: '2 ตัวบน', payout: 100, discount: 0, order: 1 },
  { name: '3 ตัวบน', payout: 1000, discount: 0, order: 2 },
  { name: '3 ตัวโต๊ด', payout: 150, discount: 0, order: 3 },
  { name: '2 ตัวโต๊ด', payout: 13, discount: 0, order: 4 },
  { name: 'วิ่งบน', payout: 3.2, discount: 0, order: 5 },
  { name: 'วิ่งล่าง', payout: 4.2, discount: 0, order: 6 },
  { name: '2 ตัวล่าง', payout: 100, discount: 0, order: 7 },
  { name: '4 ตัวบน', payout: 5000, discount: 0, order: 9 },
  { name: '4 ตัวโต๊ด', payout: 40, discount: 0, order: 10 },
  { name: 'เลขปัก', payout: 8, discount: 0, order: 11 },
]

const EXCLUDE_FIXED_POSITION_NAMES = ['ปักหลักหน่วย', 'ปักหลักสิบ', 'ปักหลักร้อย']
const FIXED_POSITION_CODES = ['FIXED_POSITION_ONES', 'FIXED_POSITION_TENS', 'FIXED_POSITION_HUNDREDS']

function resolveSortOrder(config: PrizeConfigForPayoutTable, arrayIndex: number): number {
  if (typeof config.displayOrder === 'number' && Number.isFinite(config.displayOrder)) {
    return config.displayOrder
  }
  return PRIZE_TYPE_DISPLAY_ORDER[config.prizeTypeCode] ?? 999 + arrayIndex
}

export function buildPayoutRatesTableData(
  prizeConfigs?: PrizeConfigForPayoutTable[] | null
): PayoutRatesTableRow[] {
  if (!prizeConfigs || prizeConfigs.length === 0) {
    return FALLBACK_ROWS
  }

  let mappedData = prizeConfigs
    .map((config, arrayIndex) => ({ config, arrayIndex }))
    .filter(({ config }) => config.isActive !== false && config.payoutRate > 0)
    .filter(({ config }) => {
      const name = resolvePrizeTypeLabel(config.prizeTypeCode, config.prizeTypeName)
      return !EXCLUDE_FIXED_POSITION_NAMES.includes(name)
    })
    .map(({ config, arrayIndex }) => ({
      name: resolvePrizeTypeLabel(config.prizeTypeCode, config.prizeTypeName),
      payout: config.payoutRate || 0,
      discount: config.discount || 0,
      order: resolveSortOrder(config, arrayIndex),
      arrayIndex,
    }))
    .sort((left, right) => left.order - right.order || left.arrayIndex - right.arrayIndex)
    .map(({ arrayIndex: _arrayIndex, ...row }) => row)

  const hasFixedPosition = prizeConfigs.some((config) =>
    FIXED_POSITION_CODES.includes(config.prizeTypeCode)
  )
  if (hasFixedPosition) {
    const fixedRate =
      prizeConfigs.find((config) => FIXED_POSITION_CODES.includes(config.prizeTypeCode))?.payoutRate ?? 8
    if (!mappedData.some((row) => row.name === 'เลขปัก')) {
      const fixedOrder = prizeConfigs
        .filter((config) => FIXED_POSITION_CODES.includes(config.prizeTypeCode))
        .map((config, index) => resolveSortOrder(config, index))
        .sort((left, right) => left - right)[0]
      mappedData.push({
        name: 'เลขปัก',
        payout: fixedRate,
        discount: 0,
        order: fixedOrder ?? 11,
      })
      mappedData = mappedData.sort((left, right) => left.order - right.order)
    }
  }

  return mappedData
}
